import { message } from 'antd';
import endpointUrls from 'global/Constants/endpointUrls';
import { NoPackagingRequiredHeavy, NoPackagingRequiredPacket, NoPackagingRequiredPocket } from 'global/Constants/globalConstants';
import { handleApiThenGeneric } from 'global/helperFunctions/handleApiThen';
import { localApiCatcher } from 'global/helperFunctions/localApiCatcher';
import { IBackendGetGeneric, IBackendSearchGeneric } from 'global/Models/globalModels';
import { packagingContent, packagingType } from 'global/Models/packaging';
import ApiService, { errorResponse } from 'global/utils/ApiService';

export const getPackagingTypeList = async (packagingGroupId: number | string, setPackagingTypeList: React.Dispatch<React.SetStateAction<packagingType[] | undefined>>) => {
  await ApiService.post(endpointUrls.pricingPackagingTypesSearch, { packagingGroupId, recordsPerPage: 50 })
    .then((res: IBackendSearchGeneric<packagingType>) =>
      handleApiThenGeneric<IBackendSearchGeneric<packagingType>, packagingType[]>({
        res,
        notifFail: false,
        notifSuccess: false,
        onSuccessData: (data) => {
          const collator = new Intl.Collator('fa', { numeric: true, sensitivity: 'base' });
          const packagingTypeList = [...data].sort((a, b) => collator.compare(a.title ?? '', b.title ?? '')).map((item, idx) => ({ ...item, order: idx + 1 }));

          if (packagingGroupId === 10) {
            packagingTypeList.unshift(NoPackagingRequiredPocket);
          } else if (packagingGroupId === 20) {
            packagingTypeList.unshift(NoPackagingRequiredPacket);
          } else if (packagingGroupId === 60) {
            packagingTypeList.unshift(NoPackagingRequiredHeavy);
          }

          setPackagingTypeList(packagingTypeList.sort((a: packagingType, b: packagingType) => a.order - b.order));
        },
      })
    )
    .catch(() => localApiCatcher(errorResponse));
};

export const getAllPackagingContent = async (setPackagingContent: React.Dispatch<React.SetStateAction<packagingContent[] | undefined>>) => {
  await ApiService.post(endpointUrls.pricingParcelContentTypeSearch, { recordsPerPage: 50 })
    .then((res: IBackendSearchGeneric<packagingContent>) =>
      handleApiThenGeneric<IBackendSearchGeneric<packagingContent>, packagingContent[]>({
        res,
        onSuccess: (res) => {
          setPackagingContent(res.data);
        },
      })
    )
    .catch(() => localApiCatcher(errorResponse));
};

export const getPackagingContent = async (id: string | number, setPackagingContent: React.Dispatch<React.SetStateAction<packagingContent[] | undefined>>) => {
  await ApiService.post(endpointUrls.pricingParcelContentTypeSearch, { packagingGroupId: id, recordsPerPage: 50 })
    .then((res: IBackendSearchGeneric<packagingContent>) =>
      handleApiThenGeneric<IBackendSearchGeneric<packagingContent>, packagingContent[]>({
        res,
        onSuccess: (res) => {
          setPackagingContent(res.data);
        },
      })
    )
    .catch(() => localApiCatcher(errorResponse));
};

export const packageFormValidation = (values: Record<string, any>, selectedPackagingType: packagingType): boolean => {
  let isValid = true;

  if (values.price > selectedPackagingType.maxPrice || values.price < selectedPackagingType.minPrice) {
    message.error('ارزش تعیین شده در محدوده مجاز نیست.');
    isValid = false;
  }

  if (values.length > selectedPackagingType.maxLength || values.length < selectedPackagingType.minLength) {
    message.error('طول تعیین شده در محدوده مجاز نیست.');
    isValid = false;
  }

  if (values.width > selectedPackagingType.maxWidth || values.width < selectedPackagingType.minWidth) {
    message.error('عرض تعیین شده در محدوده مجاز نیست.');
    isValid = false;
  }

  if (selectedPackagingType.maxHeight !== 0) {
    if (values.height > selectedPackagingType.maxHeight || values.height < selectedPackagingType.minHeight) {
      message.error('ارتفاع تعیین شده در محدوده مجاز نیست.');
      isValid = false;
    }
  }

  if (values.weight > selectedPackagingType.maxWeight || values.weight == 0) {
    message.error('وزن تعیین شده در محدوده مجاز نیست.');
    isValid = false;
  }

  return isValid;
};

export const getPackageFormErrors = (values: Record<string, any>, selectedPackagingType: packagingType): { name: string; message: string }[] => {
  const warnings: { name: string; message: string }[] = [];

  if (values.price > selectedPackagingType.maxPrice || values.price < selectedPackagingType.minPrice) {
    warnings.push({ name: 'price', message: 'ارزش تعیین شده در محدوده مجاز نیست.' });
  }

  if (values.length > selectedPackagingType.maxLength || values.length < selectedPackagingType.minLength) {
    warnings.push({ name: 'length', message: 'طول تعیین شده در محدوده مجاز نیست.' });
  }

  if (values.width > selectedPackagingType.maxWidth || values.width < selectedPackagingType.minWidth) {
    warnings.push({ name: 'width', message: 'عرض تعیین شده در محدوده مجاز نیست.' });
  }

  if (selectedPackagingType.maxHeight !== 0) {
    if (values.height > selectedPackagingType.maxHeight || values.height < selectedPackagingType.minHeight) {
      warnings.push({ name: 'height', message: 'ارتفاع تعیین شده در محدوده مجاز نیست.' });
    }
  }

  if (values.weight > selectedPackagingType.maxWeight || values.weight < selectedPackagingType.minWeight) {
    warnings.push({ name: 'weight', message: 'وزن تعیین شده در محدوده مجاز نیست.' });
  }

  return warnings;
};

export const getUnusualPackagingType = async (setSelectedPackagingType: React.Dispatch<React.SetStateAction<packagingType | undefined>>) => {
  await ApiService.get(endpointUrls.packagingUnusualType)
    .then((res: IBackendGetGeneric<packagingType>) =>
      handleApiThenGeneric<IBackendGetGeneric<packagingType>, packagingType>({
        res,
        notifFail: false,
        notifSuccess: false,
        onSuccessData: setSelectedPackagingType,
      })
    )
    .catch(() => {});
};
